import { useAuthStore } from '../store/authStore';
import { subscriptionService, pricingService } from '../services';
import { useState, useEffect } from 'react';
import { useT } from '../hooks/useT';
import Button from '../components/common/Button';
import PublicNav from '../components/layout/PublicNav';

// Map browser locale / timezone to a supported pricing currency
const EUR_LOCALES = ['AT','BE','CY','DE','EE','ES','FI','FR','GR','HR','IE','IT','LT','LU','LV','MT','NL','PT','SI','SK'];
// Eurozone IANA timezones — covers cases where navigator.language has no region tag (e.g. "en" from Malta)
const EUR_TIMEZONES = new Set([
  'Europe/Vienna','Europe/Brussels','Europe/Nicosia','Europe/Tallinn','Europe/Helsinki',
  'Europe/Paris','Europe/Berlin','Europe/Athens','Europe/Dublin','Europe/Rome',
  'Europe/Riga','Europe/Vilnius','Europe/Luxembourg','Europe/Malta','Europe/Amsterdam',
  'Europe/Lisbon','Atlantic/Azores','Atlantic/Madeira','Atlantic/Canary','Africa/Ceuta',
  'Europe/Bratislava','Europe/Ljubljana','Europe/Madrid','Europe/Zagreb',
  'America/Guadeloupe','America/Martinique','America/Cayenne','Indian/Reunion','Indian/Mayotte',
]);
function guestCurrency() {
  try {
    // Iterate all language preferences — a Maltese browser may list "en-MT" even if "en" is first
    for (const lang of navigator.languages ?? [navigator.language]) {
      const region = new Intl.Locale(lang).region?.toUpperCase();
      if (region === 'BR') return 'BRL';
      if (region && EUR_LOCALES.includes(region)) return 'EUR';
    }
    // Fallback: use the OS timezone (reliable even when browser language has no region)
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'America/Sao_Paulo' || tz?.startsWith('Brazil/')) return 'BRL';
    if (EUR_TIMEZONES.has(tz)) return 'EUR';
  } catch { /* ignore */ }
  return 'USD';
}

const FREE_FEATURE_KEYS = [
  { key: 'pricing.feature.free.1', included: true },
  { key: 'pricing.feature.free.2', included: true },
  { key: 'pricing.feature.free.3', included: true },
  { key: 'pricing.feature.free.4', included: true },
  { key: 'pricing.feature.free.5', included: true },
  { key: 'pricing.feature.free.6', included: false },
  { key: 'pricing.feature.free.7', included: false },
  { key: 'pricing.feature.free.8', included: false },
  { key: 'pricing.feature.free.9', included: false },
  { key: 'pricing.feature.free.10', included: false },
  { key: 'pricing.feature.free.11', included: false },
];

const PRO_FEATURE_KEYS = [
  { key: 'pricing.feature.pro.1', included: true },
  { key: 'pricing.feature.pro.2', included: true },
  { key: 'pricing.feature.pro.3', included: true },
  { key: 'pricing.feature.pro.4', included: true },
  { key: 'pricing.feature.pro.5', included: true },
  { key: 'pricing.feature.pro.6', included: true },
  { key: 'pricing.feature.pro.7', included: true },
  { key: 'pricing.feature.pro.8', included: true },
  { key: 'pricing.feature.pro.9', included: true },
  { key: 'pricing.feature.pro.10', included: true },
  { key: 'pricing.feature.pro.11', included: true },
];


function FeatureLine({ label, included }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      <span className={included ? 'text-green-400' : 'text-slate-600'}>{included ? '✓' : '✗'}</span>
      <span className={included ? 'text-slate-300' : 'text-slate-500'}>{label}</span>
    </li>
  );
}

export default function PricingPage() {
  const { isAuthenticated, isPaid, user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [error, setError] = useState(null);
  const t = useT();

  const authenticated = isAuthenticated();
  const paid = isPaid();

  const currency = user?.currency || guestCurrency();
  const [pricing, setPricing] = useState(null);
  const [interval, setInterval] = useState('monthly');

  useEffect(() => {
    pricingService.getByCurrency(currency)
      .then(setPricing)
      .catch(() => {/* keep null, fallback rendered */});
  }, [currency]);

  const proPrice = pricing
    ? { symbol: pricing[interval]?.symbol, amount: parseFloat(pricing[interval]?.amount).toFixed(2) }
    : null;

  const savingsPct = pricing?.monthly && pricing?.yearly
    ? Math.round((1 - (parseFloat(pricing.yearly.amount) / 12) / parseFloat(pricing.monthly.amount)) * 100)
    : null;

  async function handleUpgrade() {
    if (!authenticated) {
      window.location.href = '/register';
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const { url } = await subscriptionService.checkout({ interval });
      window.location.href = url;
    } catch (err) {
      setError(err.response?.data?.error?.message || t('pricing.error.checkout'));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleManage() {
    setIsPortalLoading(true);
    setError(null);
    try {
      const { url } = await subscriptionService.portal();
      window.location.href = url;
    } catch (err) {
      setError(err.response?.data?.error?.message || t('pricing.error.portal'));
    } finally {
      setIsPortalLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface">
      <PublicNav />
      <div className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <h1 className="text-4xl font-bold text-slate-100 mb-4">{t('pricing.title')}</h1>
          <p className="text-slate-400 text-lg">
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Billing interval toggle */}
        <div className="flex items-center justify-center mb-10">
          <div className="flex items-center gap-1 bg-slate-800 rounded-xl p-1">
            <button
              onClick={() => setInterval('monthly')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                interval === 'monthly'
                  ? 'bg-brand-600 text-white shadow'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t('pricing.billing.monthly')}
            </button>
            <button
              onClick={() => setInterval('yearly')}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                interval === 'yearly'
                  ? 'bg-brand-600 text-white shadow'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t('pricing.billing.yearly')}
              {savingsPct && (
                <span className="text-xs font-semibold bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                  {t('pricing.billing.save').replace('{pct}', savingsPct)}
                </span>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-8 p-4 rounded-xl border border-red-700/50 bg-red-950/30 text-red-400 text-sm text-center">
            {error}
          </div>
        )}

        {/* Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Free */}
          <div className="card flex flex-col">
            <div className="mb-6">
              <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">{t('pricing.free.label')}</p>
              <div className="flex items-end gap-1">
                <span className="text-5xl font-bold text-slate-100">
                  {proPrice ? `${proPrice.symbol}0` : '—'}
                </span>
                <span className="text-slate-500 mb-2">{interval === 'monthly' ? t('pricing.month') : t('pricing.year')}</span>
              </div>
              <p className="text-slate-400 text-sm mt-2">{t('pricing.free.sub')}</p>
            </div>

            <ul className="space-y-2.5 mb-8 flex-1">
              {FREE_FEATURE_KEYS.map((f) => <FeatureLine key={f.key} label={t(f.key)} included={f.included} />)}
            </ul>

            <div>
              {authenticated && !paid ? (
                <div className="w-full py-2.5 rounded-xl border border-slate-600 text-slate-400 text-sm text-center">
                  {t('pricing.current_plan')}
                </div>
              ) : !authenticated ? (
                <a href="/register" className="btn-secondary block text-center w-full">
                  {t('pricing.get_started')}
                </a>
              ) : null}
            </div>
          </div>

          {/* Pro */}
          <div className="card flex flex-col border-brand-700/60 bg-brand-950/10 relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <span className="text-xs font-semibold bg-brand-600 text-white px-2.5 py-1 rounded-full">
                {t('pricing.pro.badge')}
              </span>
            </div>

            <div className="mb-6">
              <p className="text-sm font-semibold text-brand-400 uppercase tracking-wider mb-2">{t('pricing.pro.label')}</p>
              <div className="flex items-end gap-1">
                <span className="text-5xl font-bold text-slate-100">
                  {proPrice ? `${proPrice.symbol}${proPrice.amount}` : '—'}
                </span>
                <span className="text-slate-500 mb-2">{interval === 'monthly' ? t('pricing.month') : t('pricing.year')}</span>
              </div>
              {interval === 'yearly' && (
                <p className="text-green-400 text-xs font-medium mb-1">{t('pricing.yearly.billed')}</p>
              )}
              <p className="text-slate-400 text-sm mt-2">{t('pricing.pro.sub')}</p>
            </div>

            <ul className="space-y-2.5 mb-8 flex-1">
              {PRO_FEATURE_KEYS.map((f) => <FeatureLine key={f.key} label={t(f.key)} included={f.included} />)}
            </ul>

            <div>
              {paid ? (
                <Button
                  variant="secondary"
                  className="w-full"
                  isLoading={isPortalLoading}
                  onClick={handleManage}
                >
                  {t('pricing.manage')}
                </Button>
              ) : (
                <Button
                  className="w-full"
                  isLoading={isLoading}
                  onClick={handleUpgrade}
                >
                  {authenticated ? t('pricing.upgrade_auth') : t('pricing.upgrade_guest')}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-20">
          <h2 className="text-xl font-bold text-slate-200 text-center mb-8">{t('pricing.faq.title')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="card">
                <p className="font-semibold text-slate-200 mb-1">{t(`pricing.faq.${n}.q`)}</p>
                <p className="text-slate-400 text-sm">{t(`pricing.faq.${n}.a`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
